/**
 * Color Utilities
 *
 * Utility functions for working with colors in components.
 * Provides type-safe access to color values and CSS variables.
 */

import { coreColors, extendedColors, semanticColors } from './colors-palettes';

/**
 * Color palette names
 */
export type ColorPaletteName =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'neutral'
    | 'neutralVariant'
    | 'error'
    | 'destructive'
    | 'success'
    | 'warning'
    | 'info'
    | 'accentOrange'
    | 'softAccent'
    | 'slate';

/**
 * Color scale values (tonal palette steps)
 */
export type ColorScale =
    | '0'
    | '5'
    | '10'
    | '15'
    | '20'
    | '25'
    | '30'
    | '35'
    | '40'
    | '50'
    | '60'
    | '70'
    | '80'
    | '85'
    | '90'
    | '95'
    | '98'
    | '99'
    | '100';

/**
 * Get a CSS variable reference for a color
 *
 * @param palette - The color palette name
 * @param scale - The tonal scale value
 * @returns CSS var() string
 *
 * @example
 * getColorVar('primary', '100') // 'var(--color-primary-100)'
 * getColorVar('error', '50') // 'var(--color-error-50)'
 */
export function getColorVar(palette: ColorPaletteName, scale: ColorScale): string {
    // Convert camelCase to kebab-case for CSS variable names
    const cssName = palette.replace(/([A-Z])/g, '-$1').toLowerCase();
    return `var(--color-${cssName}-${scale})`;
}

/**
 * Get semantic color CSS variable
 *
 * @param name - Semantic color name
 * @returns CSS var() string
 */
export function getSemanticColorVar(
    name:
        | 'surface'
        | 'on-surface'
        | 'surface-variant'
        | 'background'
        | 'on-background'
        | 'outline'
        | 'outline-variant'
        | 'primary-container'
        | 'on-primary-container'
        | 'error-container'
        | 'on-error-container'
        | 'success-container'
        | 'on-success-container'
): string {
    return `var(--color-${name})`;
}

/**
 * Get text color CSS variable
 *
 * @param variant - Text color variant
 * @returns CSS var() string
 */
export function getTextColorVar(variant: 'primary' | 'secondary' | 'tertiary' | 'disabled' | 'inverse'): string {
    return `var(--color-text-${variant})`;
}

/**
 * Get border color CSS variable
 *
 * @param variant - Border color variant
 * @returns CSS var() string
 */
export function getBorderColorVar(variant: 'default' | 'focused' | 'error' | 'success'): string {
    return `var(--color-border-${variant})`;
}

/**
 * Get interactive color CSS variable
 *
 * @param state - Interactive state
 * @returns CSS var() string
 */
export function getInteractiveColorVar(state: 'default' | 'hover' | 'pressed' | 'disabled'): string {
    return `var(--color-interactive-${state})`;
}

/**
 * Get surface container color CSS variable
 *
 * @param level - Container level
 * @returns CSS var() string
 */
export function getSurfaceContainerVar(level: 'lowest' | 'low' | 'default' | 'high' | 'highest'): string {
    if (level === 'default') {
        return 'var(--color-surface-container)';
    }
    return `var(--color-surface-container-${level})`;
}

/**
 * Core brand colors object (direct values)
 */
export const brandColors = {
    primary: coreColors.primary,
    secondary: coreColors.secondary,
    tertiary: coreColors.tertiary,
    error: coreColors.error,
    success: coreColors.success,
    warning: coreColors.warning,
    info: coreColors.info,
} as const;

/**
 * Get core brand color
 */
export function getBrandColor(name: keyof typeof brandColors): string {
    return brandColors[name];
}

/**
 * State colors for component states
 */
export const stateColors = {
    success: extendedColors.success,
    successLight: extendedColors.successLight,
    warning: extendedColors.warning,
    warningLight: extendedColors.warningLight,
    info: extendedColors.info,
    infoLight: extendedColors.infoLight,
} as const;

/**
 * Semantic text colors
 */
export const textColors = {
    primary: semanticColors.textPrimary,
    secondary: semanticColors.textSecondary,
    tertiary: semanticColors.textTertiary,
    disabled: semanticColors.textDisabled,
    inverse: semanticColors.textInverse,
} as const;

/**
 * Semantic background colors
 */
export const backgroundColors = {
    primary: semanticColors.backgroundPrimary,
    secondary: semanticColors.backgroundSecondary,
    tertiary: semanticColors.backgroundTertiary,
    disabled: semanticColors.backgroundDisabled,
} as const;

/**
 * Color role to CSS variable mapping for components
 */
export const colorRoleToVar = {
    primary: 'var(--color-primary-100)',
    secondary: 'var(--color-secondary-100)',
    tertiary: 'var(--color-tertiary-100)',
    destructive: 'var(--color-error-50)',
    success: 'var(--color-success-40)',
    warning: 'var(--color-warning-50)',
    info: 'var(--color-info-30)',
} as const;

export type ColorRoleKey = keyof typeof colorRoleToVar;
